import { useTranslation } from "react-i18next";

/**
 * Profile Skeleton Component
 * Shows skeleton loading state for personalization layout
 */
export const ProfileSkeleton: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-5 md:gap-8">
      <div className="flex flex-col gap-5 pb-5 border-b border-border-secondary dark:border-gray-600">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100 leading-7">
            {t("settings.profile.title")}
          </h2>
          <p className="text-sm text-text-tertiary dark:text-gray-400 leading-5">
            {t("settings.profile.description")}
          </p>
        </div>
      </div>

      {/* Identity skeleton */}
      <div className="rounded-2xl border border-border-secondary dark:border-gray-600 bg-white dark:bg-gray-800 p-5 md:p-7">
        <div className="flex flex-col gap-2 mb-6">
          <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-3 w-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="mx-auto lg:mx-0 h-20 w-20 sm:h-32 sm:w-32 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          </div>
        </div>
      </div>

      {/* Gender skeleton */}
      <div className="flex flex-col gap-4">
        <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-20 sm:h-28 rounded-2xl bg-gray-200 dark:bg-gray-700 animate-pulse"
            />
          ))}
        </div>
      </div>

      {/* Fit profile skeleton */}
      <div className="flex flex-col gap-4">
        <div className="h-4 w-36 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        <div className="rounded-2xl border border-border-secondary dark:border-gray-600 bg-white dark:bg-gray-800 p-4 md:p-6">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="h-20 rounded-xl bg-gray-200 dark:bg-gray-700 animate-pulse"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Contact skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="h-28 rounded-2xl bg-gray-200 dark:bg-gray-700 animate-pulse" />
        <div className="h-28 rounded-2xl bg-gray-200 dark:bg-gray-700 animate-pulse" />
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <div className="h-10 w-full sm:w-20 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        <div className="h-10 w-full sm:w-28 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
      </div>
    </div>
  );
};
